/**
 * Утилита для автоматической адаптации размера текста
 * с использованием ResizeObserver для максимальной производительности
 */

/**
 * Регулирует размер шрифта элемента для оптимального использования пространства
 * @param {HTMLElement} textElement - Элемент с текстом
 * @param {Object} options - Опции настройки
 */
const adjustTextSize = (textElement, options = {}) => {
    const {
        minSize = 8,           // Минимальный размер в пикселях
        maxSize = 24,          // Максимальный размер
        step = 0.5,            // Шаг изменения в пикселях
        unit = 'px'            // Единица измерения
    } = options;

    if (!textElement) return;

    // Получаем исходный размер из CSS (clamp)
    const currentStyle = getComputedStyle(textElement);
    const originalSize = parseFloat(currentStyle.fontSize);

    // Проверяем переполнение
    const isOverflowing = () => {
        return textElement.scrollHeight > textElement.clientHeight ||
            textElement.scrollWidth > textElement.clientWidth;
    };

    // Проверяем, есть ли свободное место
    const hasSpace = () => {
        const heightSpace = textElement.clientHeight - textElement.scrollHeight > 2;
        const widthSpace = textElement.clientWidth - textElement.scrollWidth > 10;
        return heightSpace && widthSpace;
    };

    let currentSize = originalSize;

    // Сначала пробуем увеличить размер, если есть место
    if (!isOverflowing() && hasSpace() && currentSize < maxSize) {
        let testSize = currentSize;

        // Увеличиваем размер пока есть место и не достигли максимума
        while (testSize < maxSize) {
            testSize += step;
            textElement.style.fontSize = `${testSize}${unit}`;

            // Принудительно обновляем layout
            textElement.offsetHeight;

            // Если стал переполняться, возвращаем предыдущий размер
            if (isOverflowing()) {
                testSize -= step;
                textElement.style.fontSize = `${testSize}${unit}`;
                break;
            }

            currentSize = testSize;
        }
    }

    // Если все еще переполняется, уменьшаем
    while (isOverflowing() && currentSize > minSize) {
        currentSize -= step;
        textElement.style.fontSize = `${currentSize}${unit}`;

        // Принудительно обновляем layout для точных измерений
        textElement.offsetHeight;
    }

    // Помечаем элемент как обработанный
    textElement.dataset.textAdjusted = 'true';
};

/**
 * Создает ResizeObserver для отслеживания изменений размеров
 * @param {string} selector - CSS селектор для элементов текста
 * @param {Object} options - Опции для adjustTextSize
 * @returns {ResizeObserver} - Созданный observer
 */
export const createTextResizeObserver = (selector = '.universal-game-name', options = {}) => {
    const observer = new ResizeObserver((entries) => {
        entries.forEach((entry) => {
            const textElement = entry.target;

            // Небольшая задержка для завершения layout
            requestAnimationFrame(() => {
                adjustTextSize(textElement, options);
            });
        });
    });

    return observer;
};

/**
 * Инициализирует автоматическую адаптацию текста для всех существующих элементов
 * @param {string} selector - CSS селектор для элементов текста
 * @param {Object} options - Опции настройки
 */
export const initializeTextAdjustment = (selector = '.universal-game-name', options = {}) => {
    const elements = document.querySelectorAll(selector);

    elements.forEach(element => {
        // Первоначальная настройка
        adjustTextSize(element, options);

        // Создаем observer для каждого элемента
        const observer = createTextResizeObserver();
        observer.observe(element);

        // Сохраняем observer в элементе для возможности отключения
        element._textObserver = observer;
    });
};

/**
 * Применяет автоматическую адаптацию к новому элементу
 * @param {HTMLElement} element - Элемент для настройки
 * @param {Object} options - Опции настройки
 */
export const applyTextAdjustment = (element, options = {}) => {
    if (!element || element.dataset.textAdjusted === 'true') return;

    // Настраиваем размер
    adjustTextSize(element, options);

    // Создаем observer
    const observer = createTextResizeObserver();
    observer.observe(element);

    // Сохраняем observer
    element._textObserver = observer;
};

/**
 * Отключает наблюдение за элементом
 * @param {HTMLElement} element - Элемент для отключения
 */
export const disconnectTextObserver = (element) => {
    if (element && element._textObserver) {
        element._textObserver.disconnect();
        delete element._textObserver;
        delete element.dataset.textAdjusted;
    }
};

/**
 * Принудительно пересчитывает размер текста для элемента
 * @param {HTMLElement} element - Элемент для пересчета
 * @param {Object} options - Опции настройки
 */
export const recalculateTextSize = (element, options = {}) => {
    if (!element) return;

    // Сбрасываем CSS размер для получения исходного clamp значения
    element.style.fontSize = '';

    // Принудительно обновляем layout
    element.offsetHeight;

    // Применяем адаптацию заново
    adjustTextSize(element, options);
};

/**
 * Пересчитывает размеры всех текстовых элементов на странице
 */
export const recalculateAllTextSizes = () => {
    // Пересчитываем названия игр
    const gameNames = document.querySelectorAll('.universal-game-name[data-text-adjusted="true"]');
    gameNames.forEach(element => {
        recalculateTextSize(element, TEXT_ADJUSTMENT_PRESETS.gameName);
    });

    // Пересчитываем провайдеров
    const gameProviders = document.querySelectorAll('.universal-game-provider[data-text-adjusted="true"]');
    gameProviders.forEach(element => {
        recalculateTextSize(element, TEXT_ADJUSTMENT_PRESETS.gameProvider);
    });

    // Пересчитываем placeholder тексты
    const placeholders = document.querySelectorAll('.universal-game-placeholder-title[data-text-adjusted="true"]');
    placeholders.forEach(element => {
        recalculateTextSize(element, TEXT_ADJUSTMENT_PRESETS.placeholder);
    });
};

/**
 * Настройки по умолчанию для разных типов текста
 */
export const TEXT_ADJUSTMENT_PRESETS = {
    gameName: {
        minSize: 10,
        maxSize: 28,        // Увеличили с 22 до 28
        step: 0.5
    },
    gameProvider: {
        minSize: 8,
        maxSize: 16,        // Увеличили с 14 до 16
        step: 0.25
    },
    placeholder: {
        minSize: 12,
        maxSize: 24,        // Увеличили с 20 до 24
        step: 0.5
    }
};
